import {
  createFileRoute,
  Outlet,
  redirect,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { requireSessionClient } from "@/lib/services/auth-guard";
import { lazy, Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { DashboardSidebarContent } from "@/components/DashboardSidebar";
import { AppShell as DsAppShell, MerchantMobileNavHub, Sidebar as DsSidebar } from "@/components/ds";
import { MarketBottomNav } from "@/components/MarketBottomNav";
import { MarketTenantMobileTopBar } from "@/components/clinical";
import { Button } from "@/components/ui/button";
import { useSession } from "@/hooks/use-session";
import type { BusinessProfile } from "@/lib/market-catalog";
import {
  clearStoredTenantId,
  clientRequestHeaders,
  getStoredTenantId,
  isSuperAdminInApp,
  resolveWorkspaceTenantId,
  setStoredTenantId,
  setSuperAdminInApp,
} from "@/lib/sm-api";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSidebarCollapse } from "@/hooks/use-sidebar-collapse";
import { cn } from "@/lib/utils";
import { TenantProvider } from "@/lib/tenant-context";
import { LocationContextRoot } from "@/lib/location-context";
import { AppLocationGate } from "@/components/AppLocationGate";
import { CalendarClock } from "lucide-react";
import { WHATSAPP_LEADS_E164 } from "@/lib/public-config";
import { OneSignalProvider } from "@/components/OneSignalProvider";
import { StructuraAppLoadingScreen, FM_APP_LOADING_BRAND } from "@/components/StructuraAppLoadingScreen";
import { ImpersonationSwitcher } from "@/components/admin/ImpersonationSwitcher";
import { HelpWidgetHeaderButton } from "@/components/HelpWidget";
const HelpWidget = lazy(() => import("@/components/HelpWidget").then((m) => ({ default: m.HelpWidget })));
import { Loading } from "@/components/ds/Loading";
const RenataFloatingButton = lazy(() => import("@/components/renata/RenataFloatingButton").then((m) => ({ default: m.RenataFloatingButton })));
const CommandPalette = lazy(() => import("@/components/renata/CommandPalette").then((m) => ({ default: m.CommandPalette })));
const ExperienceAdoptionBeacon = lazy(() => import("@/components/renata/ExperienceAdoptionBeacon").then((m) => ({ default: m.ExperienceAdoptionBeacon })));
import {
  TENANT_MOBILE_BOTTOM_PADDING,
  marketMobilePageTitle,
  tenantBannerOffsetOnly,
  tenantMobileShellOffset,
  tenantMobileShellTopClass,
} from "@/lib/tenant-mobile-shell";

/**
 * Rutas /app/* migradas al nuevo AppShell/Sidebar de `@/components/ds` (F4+).
 * Agregar acá al migrar cada dominio; el resto sigue usando el shell legacy.
 */
const DS_SHELL_ROUTES = new Set([
  "/app/dashboard",
  // Catálogo (F5)
  "/app/catalogacion",
  "/app/catalogacion/importar",
  "/app/catalogacion/precios",
  "/app/productos",
  "/app/categorias",
  "/app/marcas",
  "/app/combos",
  "/app/ingredientes",
  "/app/inventario",
  // Ventas (F6)
  "/app/ordenes",
  "/app/pedidos",
  "/app/pesaje",
  "/app/ordenes/devoluciones",
  // Clientes (F7)
  "/app/clientes",
  // Marketing / Branding (G1)
  "/app/descuentos",
  "/app/cupones",
  "/app/promociones",
  "/app/ruleta",
  "/app/fidelizacion",
  "/app/media",
  // B2B / Mayorista (H3)
  "/app/produccion",
  "/app/produccion/canales",
  "/app/produccion/tablero",
  "/app/produccion/pedidos",
  "/app/produccion/usuarios",
  // Compras / Abastecimiento / Operaciones (H2)
  "/app/operaciones",
  "/app/compras",
  "/app/compras/ordenes",
  "/app/compras/lotes",
  "/app/operaciones/proveedores",
  "/app/operaciones/recompras",
  // Salón (H4)
  "/app/salon",
  "/app/salon/comanda",
  "/app/salon/split-check",
  // POS — solo pantallas administrativas (H5). /app/pos/caja queda excluido: kiosk touch-first.
  "/app/pos",
  "/app/pos/movimientos",
  "/app/pos/reporte",
  // Impresoras (H6) — /app/settings sigue con shell legacy, solo esta sub-ruta migra.
  "/app/settings/printers",
  // Hardware Integration Platform
  "/app/hardware",
]);
/** Prefijos de rutas dinámicas migradas (ej. /app/ingredientes/:id/composicion). */
const DS_SHELL_ROUTE_PREFIXES = ["/app/ingredientes/", "/app/renata", "/app/pedidos/"];

function isDsShellRoute(pathname: string): boolean {
  if (DS_SHELL_ROUTES.has(pathname)) return true;
  return DS_SHELL_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

interface TenantInfo {
  name: string;
  slug: string;
  subdomain?: string | null;
  onboardingComplete?: boolean;
  onboardingStep?: number;
  trialEndsAt?: string | null;
  trialSource?: string | null;
  isDemo?: boolean;
}

interface AccessInfo {
  ok: boolean;
  code?: string | null;
  message?: string | null;
}

const SUBSCRIPTION_ACCESS_CACHE_KEY = "sm_subscription_access_v1";
const SUBSCRIPTION_ACCESS_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

type CachedSubscriptionAccess = AccessInfo & { savedAt: number; tenantId: string };

type SubscriptionApiPayload = {
  operational?: boolean;
  code?: string | null;
  message?: string | null;
};

const SUBSCRIPTION_CHECK_FAILED: AccessInfo = {
  ok: false,
  code: "SUBSCRIPTION_CHECK_FAILED",
  message: "No pudimos verificar tu plan. Revisá tu conexión e intentá de nuevo.",
};

function readCachedSubscriptionAccess(tenantId: string): AccessInfo | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(SUBSCRIPTION_ACCESS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedSubscriptionAccess;
    if (parsed.tenantId !== tenantId) return null;
    if (Date.now() - parsed.savedAt > SUBSCRIPTION_ACCESS_CACHE_TTL_MS) return null;
    return { ok: parsed.ok, code: parsed.code ?? null, message: parsed.message ?? null };
  } catch {
    return null;
  }
}

function writeCachedSubscriptionAccess(tenantId: string, access: AccessInfo): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    const payload: CachedSubscriptionAccess = { ...access, savedAt: Date.now(), tenantId };
    sessionStorage.setItem(SUBSCRIPTION_ACCESS_CACHE_KEY, JSON.stringify(payload));
  } catch {
    /* quota / private mode */
  }
}

function accessFromSubscriptionPayload(sData: SubscriptionApiPayload): AccessInfo {
  return {
    ok: sData.operational !== false,
    code: sData.code ?? null,
    message: sData.message ?? null,
  };
}

function AppNotFound() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center p-8 text-center">
      <h2 className="text-lg font-semibold">Sección no encontrada</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        La ruta no existe en el panel. Volvé al inicio del dashboard.
      </p>
      <Button className="mt-4" variant="outline" onClick={() => window.location.assign("/app/dashboard")}>
        Ir al dashboard
      </Button>
    </div>
  );
}

export const Route = createFileRoute("/app")({
  beforeLoad: async () => {
    // SSR: window no definido — omitir auth check; el cliente toma el control en hydration.
    // El check real (fetch /api/auth/session) depende de document.cookie y localStorage.
    if (typeof window === "undefined") return;
    const ok = await requireSessionClient();
    if (!ok) {
      throw redirect({
        to: "/acceso/comercio",
        search: { returnTo: undefined, error: undefined },
      });
    }
  },
  component: DashboardLayout,
  notFoundComponent: AppNotFound,
});

function DashboardLayout() {
  const navigate = useNavigate();
  const isNarrow = useIsMobile();
  const [helpOpen, setHelpOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // SCRUM-252: sidebar colapsable — preferencia local persistida (no es un flag de producto).
  const { collapsed: sidebarCollapsed, toggle: toggleSidebarCollapsed } = useSidebarCollapse();
  const location = useLocation();
  const { user, isLoading, logout } = useSession();
  const isOnboardingRoute = location.pathname === "/app/onboarding";

  useEffect(() => {
    if (!isNarrow) setMobileNavOpen(false);
  }, [isNarrow]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  const superAdminInApp = isSuperAdminInApp();
  const storedTenantId = getStoredTenantId();

  const tid = useMemo(() => {
    if (!user) return null;
    return resolveWorkspaceTenantId(user, { superAdminInApp, storedTenantId });
  }, [user, superAdminInApp, storedTenantId]);

  const [tenantInfo, setTenantInfo]     = useState<TenantInfo | null>(null);
  const [businessProfile, setBusinessProfile] = useState<BusinessProfile>("catalog");
  const [productFlags, setProductFlags] = useState<Record<string, boolean>>({});
  const [planId, setPlanId]             = useState<string | null>(null);
  const [access, setAccess]             = useState<AccessInfo>({ ok: true });
  const [accessLoading, setAccessLoading] = useState(true);
  const [accessCheckUncertain, setAccessCheckUncertain] = useState(false);
  const [accessReloadNonce, setAccessReloadNonce] = useState(0);
  const [deferredGlobalSurfacesReady, setDeferredGlobalSurfacesReady] = useState(false);

  useEffect(() => {
    if (!user?.tenantId) return;
    if ((user.role === "super_admin" || user.role === "support") && isSuperAdminInApp()) return;
    setStoredTenantId(user.tenantId);
  }, [user?.tenantId, user?.role]);

  useEffect(() => {
    if (!user?.id) return;
    void import("@/lib/product-knowledge/ui-preferences-sync").then(({ syncTourPrefsFromServer }) =>
      syncTourPrefsFromServer(),
    );
  }, [user?.id]);

  // SCRUM-508/510: command palette, adoption telemetry and Renata are not part of
  // the first navigation task. Defer their chunks until the browser is idle, while
  // still loading immediately when the user explicitly asks for Cmd/Ctrl+K.
  useEffect(() => {
    let done = false;
    const activate = () => {
      if (done) return;
      done = true;
      setDeferredGlobalSurfacesReady(true);
    };
    const idleId =
      "requestIdleCallback" in window
        ? window.requestIdleCallback(activate, { timeout: 1200 })
        : window.setTimeout(activate, 1200);
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") activate();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      if (typeof idleId === "number") {
        if ("cancelIdleCallback" in window) window.cancelIdleCallback(idleId);
        else window.clearTimeout(idleId);
      }
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!tid) {
      setAccessLoading(false);
      setAccessCheckUncertain(false);
      return;
    }
    const h = clientRequestHeaders(tid);
    setAccessLoading(true);
    setAccessCheckUncertain(false);

    const applySubscriptionAccess = (sData: SubscriptionApiPayload | null) => {
      if (sData == null) {
        const cached = readCachedSubscriptionAccess(tid);
        if (cached) {
          setAccess(cached);
          setAccessCheckUncertain(true);
        } else {
          setAccess(SUBSCRIPTION_CHECK_FAILED);
          setAccessCheckUncertain(false);
        }
        return;
      }
      const next = accessFromSubscriptionPayload(sData);
      setAccess(next);
      setAccessCheckUncertain(false);
      writeCachedSubscriptionAccess(tid, next);
    };

    void Promise.all([
      fetch("/api/tenant", { credentials: "include", headers: h }).then((r) =>
        r.ok ? r.json() as Promise<{ tenant?: TenantInfo & { productFlags?: Record<string, boolean> }; planId?: string | null; businessProfile?: string }> : null,
      ),
      fetch("/api/subscriptions", { credentials: "include", headers: h }).then((r) =>
        r.ok ? (r.json() as Promise<SubscriptionApiPayload>) : null,
      ),
    ])
      .then(([tData, sData]) => {
        if (tData?.tenant) setTenantInfo(tData.tenant);
        if (tData?.planId) setPlanId(tData.planId);
        const flags = tData?.tenant?.productFlags ?? {};
        setProductFlags(flags);
        if (tData?.businessProfile) setBusinessProfile(tData.businessProfile as BusinessProfile);
        applySubscriptionAccess(sData);
      })
      .catch(() => {
        applySubscriptionAccess(null);
      })
      .finally(() => setAccessLoading(false));
  }, [tid, accessReloadNonce]);

  const trialDaysLeft = useMemo(() => {
    if (!tenantInfo?.trialEndsAt) return null;
    const end   = new Date(tenantInfo.trialEndsAt + "T23:59:59");
    const today = new Date();
    const diff  = Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return diff >= 0 ? diff : null;
  }, [tenantInfo?.trialEndsAt]);

  const tenantCtx = useMemo(() => ({
    tenantId:   tid ?? "",
    tenantName: tenantInfo?.name ?? "Mi tienda",
    tenantSlug:  tenantInfo?.slug ?? "",
    userName:   user?.name ?? "",
    userEmail:  user?.email ?? "",
    userRole:   user?.role ?? null,
    userAvatarUrl: user?.avatarUrl ?? null,
    themeMode: (user?.themeMode as "dark" | "light" | "system" | null) ?? "dark",
    logout,
    businessProfile,
    productFlags,
  }), [tid, tenantInfo, user, logout, businessProfile, productFlags]);

  // Gate redirects once: TanStack Navigate re-fires every render (new props) and with
  // tenant-host rewrite that nests commitLocation/load/startTransition forever.
  const gateRedirectRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (isLoading || accessLoading) return;
    if (!user) {
      if (gateRedirectRef.current === "login") return;
      gateRedirectRef.current = "login";
      void navigate({
        to: "/acceso/comercio",
        search: { returnTo: undefined, error: undefined },
        replace: true,
      });
      return;
    }
    if ((user.role === "super_admin" || user.role === "support") && !isSuperAdminInApp()) {
      if (gateRedirectRef.current === "admin") return;
      gateRedirectRef.current = "admin";
      void navigate({ to: "/admin", replace: true, search: {} as never });
      return;
    }
    if (user.role === "owner" && !user.tenantId && !isOnboardingRoute) {
      if (gateRedirectRef.current === "onboarding") return;
      gateRedirectRef.current = "onboarding";
      void navigate({ to: "/app/onboarding", replace: true });
      return;
    }
    gateRedirectRef.current = null;
  }, [isLoading, accessLoading, user, isOnboardingRoute, navigate]);

  if (isLoading || accessLoading) {
    return (
      <StructuraAppLoadingScreen
        {...FM_APP_LOADING_BRAND}
        message="Validando sesión…"
      />
    );
  }
  if (!user) {
    return (
      <StructuraAppLoadingScreen
        {...FM_APP_LOADING_BRAND}
        message="Redirigiendo…"
      />
    );
  }

  if ((user.role === "super_admin" || user.role === "support") && !isSuperAdminInApp()) {
    return (
      <StructuraAppLoadingScreen
        {...FM_APP_LOADING_BRAND}
        message="Redirigiendo…"
      />
    );
  }

  // Si ya intentamos /app/onboarding y volvimos (flag off / bounce), no bloquear el shell.
  if (
    user.role === "owner" &&
    !user.tenantId &&
    !isOnboardingRoute &&
    gateRedirectRef.current !== "onboarding"
  ) {
    return (
      <StructuraAppLoadingScreen
        {...FM_APP_LOADING_BRAND}
        message="Redirigiendo…"
      />
    );
  }

  const ownerOnboardingRelax =
    isOnboardingRoute &&
    user.role === "owner" &&
    (!tid || !tenantInfo?.onboardingComplete);

  if (!ownerOnboardingRelax && !access.ok) {
    const isTrialExpired = ["TRIAL_EXPIRED", "no_subscription", "subscription_expired", "subscription_inactive"].includes(
      access.code ?? "",
    );
    const isSubscriptionCheckFailed = access.code === "SUBSCRIPTION_CHECK_FAILED";
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="max-w-md rounded-xl border border-border bg-card p-8 text-center">
          {isSubscriptionCheckFailed ? (
            <>
              <h1 className="text-lg font-bold text-foreground">No pudimos verificar tu plan</h1>
              <p className="mt-3 text-sm text-muted-foreground">{access.message}</p>
              <Button
                type="button"
                className="mt-6"
                onClick={() => setAccessReloadNonce((n) => n + 1)}
              >
                Reintentar
              </Button>
            </>
          ) : isTrialExpired ? (
            <>
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/10 text-2xl">📋</div>
              <h1 className="text-lg font-bold text-foreground">Tu período de prueba finalizó</h1>
              <p className="mt-3 text-sm text-muted-foreground">
                Para seguir usando Structura Market, activá tu suscripción.
              </p>
              <a
                href={`https://wa.me/${WHATSAPP_LEADS_E164}?text=Hola%2C%20quiero%20contratar%20Structura%20Market`}
                target="_blank"
                rel="noreferrer"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
              >
                💬 Contratar plan →
              </a>
            </>
          ) : (
            <>
              <h1 className="text-lg font-bold text-foreground">Sistema no disponible</h1>
              <p className="mt-2 text-sm text-muted-foreground">{access.message}</p>
              <Button variant="outline" className="mt-6" onClick={() => logout()}>
                Cerrar sesión
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (isOnboardingRoute) {
    return (
      <div className="min-h-screen bg-background">
        <Outlet />
      </div>
    );
  }

  const tenantLabel = tenantInfo?.name ?? "Mi tienda";
  const tenantSlug  = tenantInfo?.slug ?? "";
  const tenantSubdomain = tenantInfo?.subdomain ?? tenantSlug;
  const isImpersonating = (user.role === "super_admin" || user.role === "support") && isSuperAdminInApp() && !!tid;
  const isTrial = trialDaysLeft !== null;
  const trialUrgent = isTrial && (trialDaysLeft ?? 99) <= 3;
  const hasTopBanner = isImpersonating || isTrial || accessCheckUncertain;
  // Demo y admin ahora van en una sola barra: hasDemoBar solo afecta offsets cuando NO hay impersonación
  const hasDemoBar = !!tenantInfo?.isDemo && !isImpersonating;
  const mobileShellTopClass = tenantMobileShellTopClass({ hasDemoBar, hasTopBanner });
  const mobileShellOffset = tenantMobileShellOffset({ hasDemoBar, hasTopBanner });
  const mobilePageTitle = marketMobilePageTitle(location.pathname);

  const showSetupBanner =
    !!tenantInfo &&
    !tenantInfo.onboardingComplete &&
    (tenantInfo.onboardingStep ?? 0) < 4 &&
    user.role !== "staff";

  return (
    <TenantProvider value={tenantCtx}>
      <LocationContextRoot tenantId={tid ?? ""}>
      <AppLocationGate operationalRoleId={user.operationalRoleId} locationRoles={user.locationRoles}>
      {(effectiveOperationalRoleId) => (
      <div className="relative min-h-screen bg-background">
        <div
          className="pointer-events-none fixed right-[-5%] top-[-10%] -z-10 h-[40%] w-[40%] rounded-full bg-primary/5 blur-[120px]"
          aria-hidden
        />
        <div
          className="pointer-events-none fixed bottom-[-10%] left-[15%] -z-10 h-[30%] w-[30%] rounded-full bg-secondary/10 blur-[100px]"
          aria-hidden
        />

        {/* ── Barra admin / demo — una sola línea compacta, nunca tapa contenido ── */}
        {(tenantInfo?.isDemo || isImpersonating) && (
          <div className="fixed left-0 right-0 top-0 z-[9999] flex items-center justify-between gap-2 bg-amber-500/95 px-3 py-0.5 text-[11px] font-medium text-amber-950 backdrop-blur">
            <span className="flex min-w-0 items-center gap-2 truncate">
              {tenantInfo?.isDemo && (
                <span className="shrink-0 rounded bg-violet-600 px-1.5 py-px text-[10px] font-semibold text-white">DEMO</span>
              )}
              {isImpersonating && (
                <>
                  <span className="shrink-0 opacity-60">Vista admin:</span>
                  <strong className="truncate">{tenantInfo?.name ?? tid}</strong>
                  {tid && <ImpersonationSwitcher currentTenantId={tid} />}
                </>
              )}
              {tenantInfo?.isDemo && !isImpersonating && (
                <span className="opacity-70">Los datos se reinician periódicamente</span>
              )}
            </span>
            {isImpersonating && (
              <Button
                size="sm"
                variant="ghost"
                className="h-5 shrink-0 px-2 text-[11px] text-amber-950 hover:bg-amber-400"
                type="button"
                onClick={() => {
                  if (tid) {
                    void fetch(`/api/admin/impersonate?tenantId=${tid}`, {
                      method: "DELETE",
                      credentials: "include",
                      headers: clientRequestHeaders(tid),
                    }).catch(() => {});
                  }
                  clearStoredTenantId();
                  setSuperAdminInApp(false);
                  void navigate({ to: "/admin", search: {} as never });
                }}
              >
                Salir
              </Button>
            )}
          </div>
        )}

        {isTrial && !isImpersonating && (
          <div
            className={cn(
              "fixed left-0 right-0 z-[9997] flex items-center justify-between gap-2 px-4 py-1.5 text-xs font-medium",
              trialUrgent
                ? "bg-red-600/90 text-white backdrop-blur"
                : "bg-teal-700/90 text-teal-50 backdrop-blur",
              tenantInfo?.isDemo ? "top-8" : "top-0",
            )}
          >
            <span className="flex items-center gap-1.5">
              <CalendarClock className="h-3.5 w-3.5 shrink-0" />
              {trialUrgent ? (
                <>
                  Prueba gratuita: quedan{" "}
                  <strong>
                    {trialDaysLeft} día{trialDaysLeft !== 1 ? "s" : ""}
                  </strong>
                </>
              ) : (
                <>
                  Prueba gratuita ·{" "}
                  <strong>
                    {trialDaysLeft} día{trialDaysLeft !== 1 ? "s" : ""}
                  </strong>{" "}
                  restantes
                </>
              )}
            </span>
            <a
              href={`https://wa.me/${WHATSAPP_LEADS_E164}?text=Hola%2C%20quiero%20contratar%20Structura%20Market`}
              className="shrink-0 rounded bg-white/20 px-2 py-0.5 text-[11px] font-semibold transition-colors hover:bg-white/30"
            >
              Contratar →
            </a>
          </div>
        )}

        {accessCheckUncertain && access.ok ? (
          <div
            className={cn(
              "fixed left-0 right-0 z-[9996] flex items-center justify-between gap-2 bg-slate-800/95 px-4 py-1.5 text-xs font-medium text-slate-100 backdrop-blur",
              (tenantInfo?.isDemo || isImpersonating || isTrial) ? "top-6" : "top-0",
            )}
          >
            <span>No pudimos verificar tu plan. Estás viendo el último estado conocido.</span>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="h-7 shrink-0 px-2 text-[11px]"
              onClick={() => setAccessReloadNonce((n) => n + 1)}
            >
              Reintentar
            </Button>
          </div>
        ) : null}

        {isNarrow && !isOnboardingRoute ? (
          <MarketTenantMobileTopBar
            title={mobilePageTitle}
            topClassName={mobileShellTopClass}
            onMenuOpen={() => setMobileNavOpen(true)}
            helpSlot={<HelpWidgetHeaderButton open={helpOpen} onClick={() => setHelpOpen((v) => !v)} />}
          />
        ) : null}

        {isNarrow && !isOnboardingRoute ? (
          <MerchantMobileNavHub
            open={mobileNavOpen}
            onOpenChange={setMobileNavOpen}
            role={user.role}
            planId={planId}
            businessProfile={businessProfile}
            operationalRoleId={effectiveOperationalRoleId}
            topClassName={mobileShellTopClass}
          />
        ) : null}

        {(() => {
          const isRenataRoute = location.pathname.startsWith("/app/renata");
          const isRenataChat = location.pathname.startsWith("/app/renata/chat");
          const mainClassName = cn(
            "app-page min-w-0 overflow-x-hidden",
            // Solo el chat necesita viewport fijo sin scroll de página.
            // El resto de Renata debe poder scrollear (Automatizaciones, Objetivos, etc.).
            isRenataChat
              ? "flex h-dvh flex-col overflow-hidden"
              : isRenataRoute
                ? "flex min-h-dvh flex-col"
                : "min-h-screen",
            isNarrow
              ? "ml-0"
              : cn(
                  "ml-0 transition-[margin] duration-200",
                  sidebarCollapsed ? "lg:ml-[68px]" : "lg:ml-64",
                  (hasTopBanner || hasDemoBar || tenantInfo?.isDemo) && "lg:pt-6",
                ),
          );
          const mainStyle =
            isNarrow && !isOnboardingRoute
              ? ({
                  paddingTop: mobileShellOffset,
                  paddingBottom: TENANT_MOBILE_BOTTOM_PADDING,
                  ["--tenant-mobile-shell-offset" as string]: mobileShellOffset,
                } as React.CSSProperties)
              : isNarrow
                ? (() => {
                    const bannerOnly = tenantBannerOffsetOnly({ hasDemoBar, hasTopBanner });
                    return bannerOnly ? ({ paddingTop: bannerOnly } as React.CSSProperties) : undefined;
                  })()
                : undefined;

          // Experience Builder: pantalla inmersiva sin sidebar (editor visual, no panel admin).
          if (location.pathname === "/app/experience") {
            return (
              <div className="fixed inset-0 z-50 overflow-hidden bg-background">
                <Outlet />
              </div>
            );
          }

          // /app/dashboard es el primer consumidor del nuevo AppShell/Sidebar (F4).
          // El resto de las rutas /app/* sigue usando el shell actual sin cambios,
          // hasta que se migren en sprints posteriores (mismo patrón evolutivo del Experience Engine).
          if (isDsShellRoute(location.pathname)) {
            return (
              <DsAppShell
                sidebar={
                  <DsSidebar
                    role={user.role}
                    tenantId={tid ?? undefined}
                    tenantSlug={tenantSubdomain}
                    planId={planId}
                    businessProfile={businessProfile}
                    showSetupBanner={showSetupBanner}
                    operationalRoleId={effectiveOperationalRoleId}
                    collapsed={sidebarCollapsed}
                    onToggleCollapse={toggleSidebarCollapsed}
                  />
                }
                isNarrow={isNarrow}
                hasTopBanner={hasTopBanner}
                mainClassName={mainClassName}
                mainStyle={mainStyle}
                collapsed={sidebarCollapsed}
              >
                <Outlet />
              </DsAppShell>
            );
          }

          return (
            <>
              {!isNarrow && (
                <aside
                  data-sidebar="primary"
                  data-sidebar-collapsed={sidebarCollapsed ? "true" : "false"}
                  className={cn(
                    "fixed left-0 z-40 hidden flex-col border-r border-sidebar-border bg-sidebar py-3 transition-[width] duration-200 lg:flex",
                    sidebarCollapsed ? "w-[68px]" : "w-64",
                    (hasTopBanner || hasDemoBar || tenantInfo?.isDemo) ? "top-6 h-[calc(100vh-1.5rem)]" : "top-0 h-screen",
                  )}
                >
                  <DashboardSidebarContent
                    role={user.role}
                    tenantId={tid ?? undefined}
                    tenantSlug={tenantSubdomain}
                    planId={planId}
                    businessProfile={businessProfile}
                    showSetupBanner={showSetupBanner}
                    operationalRoleId={effectiveOperationalRoleId}
                    collapsed={sidebarCollapsed}
                    onToggleCollapse={toggleSidebarCollapsed}
                  />
                </aside>
              )}

              <main className={mainClassName} style={mainStyle}>
                <Outlet />
              </main>
            </>
          );
        })()}

        {isNarrow && !isOnboardingRoute && location.pathname !== "/app/experience" ? <MarketBottomNav /> : null}

        <Suspense fallback={<Loading variant="list" rows={5} />}>
          <HelpWidget
            open={isNarrow ? helpOpen : undefined}
            onOpenChange={isNarrow ? setHelpOpen : undefined}
            showFloatingTrigger={!isNarrow && productFlags.ai_assistant !== true}
          />
        </Suspense>
        {tid && !tenantInfo?.isDemo && !!(tenantInfo as unknown as Record<string, unknown> | null)?.productFlags && ((tenantInfo as unknown as Record<string, unknown>).productFlags as Record<string, unknown>)?.push_notifications === true && <OneSignalProvider tenantId={tid} />}
        {deferredGlobalSurfacesReady ? (
          <Suspense fallback={null}>
            {productFlags.ai_assistant === true && !mobileNavOpen && <RenataFloatingButton />}
            <CommandPalette />
            <ExperienceAdoptionBeacon />
          </Suspense>
        ) : null}
      </div>
      )}
      </AppLocationGate>
      </LocationContextRoot>
    </TenantProvider>
  );
}

